import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useUserSegments } from 'hooks/use-user-segments';
import { useGetMenuSectionsQuery } from 'types/graphql';

type UseMenuSectionsOptions = {
  dispensaryId?: string;
  skip?: boolean;
};

type QueryHookReturn = ReturnType<typeof useGetMenuSectionsQuery>;

type UseMenuSectionsReturn = {
  menuSections: NonNullable<QueryHookReturn['data']>['getMenuSections'];
  loading: boolean;
  error: QueryHookReturn['error'];
  refetch: QueryHookReturn['refetch'];
};

export default function useMenuSections({ dispensaryId, skip = false }: UseMenuSectionsOptions): UseMenuSectionsReturn {
  const flags = useFlags();
  const isExclusiveCollectionsEnabled = flags['ecommerce.exclusive-collections.rollout'] ?? false;

  const { segments: userSegments, loading: segmentsLoading } = useUserSegments({ dispensaryId });
  const sortedSegments = useMemo(() => [...userSegments].sort(), [userSegments]);
  const userSegmentIds = isExclusiveCollectionsEnabled && sortedSegments.length > 0 ? sortedSegments : undefined;

  const { data, loading, error, refetch } = useGetMenuSectionsQuery({
    variables: {
      dispensaryId: dispensaryId ?? '',
      userSegmentIds,
    },
    skip: skip || !dispensaryId || segmentsLoading,
    fetchPolicy: 'cache-and-network',
  });

  return {
    menuSections: data?.getMenuSections ?? [],
    loading: skip ? false : loading || segmentsLoading,
    error,
    refetch,
  };
}
